import { createAsyncThunk } from '@reduxjs/toolkit'
import { RootState } from '../store'
import { delay, getEnums, isDevelop, splitS3FilePath } from '../utils/Common'
import { BASE_URL, S3_BUCKET_NAME } from '../config'
import { getIDToken } from '../utils/AuthUtil'
import {
  addCodeValidationRulesForDiscipline,
  setCurrentValildationMessages,
  setCurrentValidationTask,
  setIsCodeValidationProcessingStarted,
  setIsCodeValidationStatusChecking,
  updateCodeValidationRulesWithStatus,
  setIsAllCodeValidationRulesFetched,
} from '../actions/validation'
import {
  CodeValidationDiscipline,
  CodeValidationRule,
  CodeValidationRuleStatus,
  CodeValidationStatus,
  TaskStatus,
} from '../models/Validation'
import { SceneManager } from '../visualization/sceneManager'
import { EventType } from '../utils/EventEmitter'

const paginationCount = 1000

type GetValidationStatusPayload = {
  autoCreateTaskIfMissing?: boolean
  autoCreateTaskIfStale?: boolean
}

type GetValidationStatusResponse = {
  taskId?: string
  status?: TaskStatus
  createdAt?: string
  updatedAt?: string
}

export const getValidationStatus = createAsyncThunk(
  'validation/getValidationStatus',
  async (
    {
      autoCreateTaskIfMissing = true,
      autoCreateTaskIfStale = false,
    }: GetValidationStatusPayload,
    { dispatch, getState },
  ) => {
    const state = getState() as RootState
    const filepath = state.organization.selectedFile?.path
    if (!filepath) {
      console.warn('Selected file is undefined.')
      return
    }

    dispatch(setIsCodeValidationStatusChecking(true))

    const { s3_folder, s3_file } = splitS3FilePath(filepath)
    const requestBody = isDevelop()
      ? {}
      : {
          auto_create_task_if_missing: autoCreateTaskIfMissing,
          auto_create_task_if_stale: autoCreateTaskIfStale,
          s3_bucketname: S3_BUCKET_NAME,
          s3_folder,
          s3_file,
        }

    const promise = isDevelop()
      ? fetch('')
      : fetch(`${BASE_URL}/api/validations/status`, {
          method: 'POST',
          headers: {
            'Access-Control-Allow-Origin': '*',
            Authorization: `Bearer ${await getIDToken()}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody),
        })

    try {
      const response = await promise
      if (!response.ok) {
        throw await response.json()
      }

      const {
        taskId,
        status,
        createdAt,
        updatedAt,
      }: GetValidationStatusResponse = await response.json()
      if (taskId && status && createdAt && updatedAt) {
        dispatch(
          setCurrentValidationTask({
            taskId,
            status,
            createdAt,
            updatedAt,
          }),
        )
      } else {
        dispatch(setCurrentValidationTask(null))
      }
    } catch (error: any) {
      console.error(
        'Error during getting status of AI Code Validation task: ',
        error,
      )
      dispatch(setCurrentValidationTask(null))
    } finally {
      dispatch(setIsCodeValidationStatusChecking(false))
    }
  },
)

type CreateTaskResponse = {
  taskId: string
  status: TaskStatus
  createdAt: string
  updatedAt: string
}

export const createValidationTask = createAsyncThunk(
  'validation/createValidationTask',
  async (_, { dispatch, getState }) => {
    const state = getState() as RootState
    const filepath = state.organization.selectedFile?.path
    if (!filepath) {
      console.warn('Selected file is undefined.')
      return
    }

    dispatch(setIsCodeValidationProcessingStarted(true))

    const { s3_folder, s3_file } = splitS3FilePath(filepath)
    const requestBody = isDevelop()
      ? {}
      : {
          s3_bucketname: S3_BUCKET_NAME,
          s3_folder,
          s3_file,
        }

    const promise = isDevelop()
      ? fetch('')
      : fetch(`${BASE_URL}/api/validations/tasks`, {
          method: 'POST',
          headers: {
            'Access-Control-Allow-Origin': '*',
            Authorization: `Bearer ${await getIDToken()}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody),
        })

    try {
      const response = await promise
      if (!response.ok) {
        throw await response.json()
      }

      const data: CreateTaskResponse = await response.json()
      dispatch(setCurrentValidationTask({ ...data }))
    } catch (error: any) {
      console.error('Error during creating AI Code Validation task: ', error)
      dispatch(setCurrentValidationTask(null))
    } finally {
      dispatch(setIsCodeValidationProcessingStarted(false))
    }
  },
)

type RulesResponse = {
  hasMore: boolean
  nextOffset: number | null
  totalItems: number
  data: CodeValidationRule[]
}

export const fetchAllCodeValidationRules = createAsyncThunk(
  'validation/fetchAllCodeValidationRules',
  async ({ signal }: { signal: AbortSignal }, { dispatch, getState }) => {
    const state = getState() as RootState
    const isAllCodeValidationRulesFetched =
      state.validation.isAllCodeValidationRulesFetched
    const disciplines = getEnums(CodeValidationDiscipline).map(
      ([_, discipline]) => discipline,
    )
    console.log(
      'isAllCodeValidationRulesFetched',
      isAllCodeValidationRulesFetched,
    )
    try {
      //if (!isAllCodeValidationRulesFetched) {
      for (const discipline of disciplines) {
        let hasMore: boolean = false
        let offset: number | null = 0

        try {
          do {
            const params = new URLSearchParams({
              limit: paginationCount.toString(),
            })
            if (offset) {
              params.set('offset', offset.toString())
            }

            const promise = isDevelop()
              ? fetch('', { signal })
              : fetch(
                  `${BASE_URL}/api/validations/rules/${discipline}?${params.toString()}`,
                  {
                    signal,
                    headers: {
                      'Access-Control-Allow-Origin': '*',
                      Authorization: `Bearer ${await getIDToken()}`,
                      'Content-Type': 'application/json',
                    },
                  },
                )

            const response = await promise
            if (!response.ok) {
              throw await response.json()
            }

            const responseJson: RulesResponse = await response.json()
            hasMore = responseJson.hasMore
            offset = responseJson.nextOffset

            responseJson.data.forEach(
              (d) => (d.status = CodeValidationStatus.Failed),
            )

            dispatch(
              addCodeValidationRulesForDiscipline({
                discipline,
                rules: responseJson.data,
              }),
            )
          } while (hasMore)
        } catch (error: any) {
          if (error.name !== 'AbortError') {
            console.error(
              `Error during fetching AI Code Validation rules for '${discipline}': `,
              error,
            )
          } else {
            throw error
          }
        }
      }
      dispatch(setIsAllCodeValidationRulesFetched(true))
      //}

      dispatch(fetchAllCodeValidationResults({ signal }))
    } catch (error: any) {
      if (error.name !== 'AbortError') {
        console.error(`Error during fetching AI Code Validation rules: `, error)
      }
    }
  },
)

type ResultsResponse = {
  isTaskDone: boolean
  hasMore: boolean
  nextOffset: number | null
  totalItems: number
  data: CodeValidationRuleStatus[]
}

export const fetchAllCodeValidationResults = createAsyncThunk(
  'validation/fetchAllCodeValidationResults',
  async ({ signal }: { signal: AbortSignal }, { getState, dispatch }) => {
    const state = getState() as RootState
    const taskId = state.validation.currentValidationTask?.taskId
    if (!taskId) {
      console.warn('Current Code Validation Task is null')
      return
    }

    try {
      let hasMore: boolean = false
      let offset: number | null = 0

      do {
        const params = new URLSearchParams({
          limit: paginationCount.toString(),
        })
        if (offset) {
          params.set('offset', offset.toString())
        }

        const promise = isDevelop()
          ? fetch('', { signal })
          : fetch(
              `${BASE_URL}/api/validations/tasks/${taskId}?${params.toString()}`,
              {
                signal,
                headers: {
                  'Access-Control-Allow-Origin': '*',
                  Authorization: `Bearer ${await getIDToken()}`,
                  'Content-Type': 'application/json',
                },
              },
            )

        const response = await promise
        if (!response.ok) {
          throw await response.json()
        }

        const responseJson: ResultsResponse = await response.json()

        dispatch(
          updateCodeValidationRulesWithStatus({ data: responseJson.data }),
        )

        await new Promise<void>((resolve) => {
          const listener = () => {
            resolve()

            SceneManager.getInstance()
              .getEventEmitter()
              ?.off(EventType.CodeValidationAnimationFinished, listener)
          }

          SceneManager.getInstance()
            .getEventEmitter()
            ?.on(EventType.CodeValidationAnimationFinished, listener)

          SceneManager.getInstance()
            .getEventEmitter()
            ?.emit(EventType.CodeValidationNewDataFetch, responseJson.data)
        })

        offset = responseJson.nextOffset
        hasMore = responseJson.hasMore
      } while (hasMore)

      dispatch(setCurrentValildationMessages(['Validation process completed']))

      await delay(5000)
    } catch (error: any) {
      if (error.name !== 'AbortError') {
        console.error(
          `Error during fetching AI Code Validation results: `,
          error,
        )
      }
    } finally {
      dispatch(setCurrentValildationMessages([]))
    }
  },
)
